import React, { ReactElement } from 'react';
import { format, parseISO } from 'date-fns';
import CheckIcon from '@mui/icons-material/CheckOutlined';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import {
  GqlAnomalyAlertsPaginated,
  GqlPlotAlertsPaginated,
  GqlPlotAlertsPaginatedContent,
  GqlPlotSensorsSystems,
} from 'gql/types/plots';
import { DEFAULT_DATE_FORMAT } from 'core/constants';
import { getAlertTypeTranslation } from 'utils/alertsUtils';
import { TFunction } from 'i18next';

const EMPTY_VALUE = 'N/A';
export const TABLE_ALERTS_MAX_ROWS_NUMBER = 50;

export const getAlertsDateColumnBody = ({
  alertSetTime,
}: GqlPlotAlertsPaginatedContent): string => {
  const formattedDate = alertSetTime
    ? format(parseISO(alertSetTime), DEFAULT_DATE_FORMAT)
    : '';

  return formattedDate;
};

export const getIgnoreColumnBody = ({
  alertIgnore,
}: GqlPlotAlertsPaginatedContent): ReactElement => {
  return alertIgnore ? <CheckIcon /> : <CloseIcon />;
};

export const getAlertTypeColumnBody = ({
  alertType,
}: GqlPlotAlertsPaginatedContent): string => {
  return getAlertTypeTranslation(alertType);
};

export const getSensorNameColumnBody = ({
  sensorName,
}: GqlPlotAlertsPaginatedContent): string => sensorName || EMPTY_VALUE;

export const getSensorSerialColumnBody = ({
  sensorSerial,
}: GqlPlotAlertsPaginatedContent): string => sensorSerial || EMPTY_VALUE;

export const getSensorTypeColumnBody = ({
  sensorType,
}: GqlPlotAlertsPaginatedContent): string => sensorType || EMPTY_VALUE;

export const getSystemSerialColumnBody = ({
  systemSerial,
}: GqlPlotAlertsPaginatedContent): string => systemSerial || EMPTY_VALUE;

export const handleCreateTechnicalData = (
  plotAlerts: GqlPlotAlertsPaginated,
  plotsSensorsSystems: GqlPlotSensorsSystems,
  translateAlerts?: { t: TFunction },
) => {
  if (!plotAlerts) {
    return plotAlerts;
  }

  if (!plotsSensorsSystems) {
    if (translateAlerts) {
      return plotAlerts.pageAlerts.content.forEach((alert) => {
        alert.alertType = translateAlerts.t((`${alert.alertType}.description`))
      });
    }

    return plotAlerts;
  }

  plotAlerts.pageAlerts.content.forEach((alert) => {
    if (translateAlerts) {
      alert.alertType = translateAlerts.t((`${alert.alertType}.description`))
    }

    plotsSensorsSystems?.plots.forEach((plot) => {
      const sensor = plot.sensors.find(
        (sensorItem) => Number(sensorItem.id) === Number(alert.sensorId),
      );


      if (sensor) {
        const depth =
          JSON.parse((sensor?.params as string) || '{}')?.depth || '';

        const alias = sensor?.alias || '';
        const sensorDetails =
          !!depth && !!alias ? `${depth},${alias}` : `${depth}${alias}`;

        alert.sensorName = sensor.displayName;
        alert.sensorSerial = sensor.serial;
        alert.sensorType = sensor.sensorType.type;
        alert.systemSerial = sensor.systemSerial;
        alert.sensorDetails = sensorDetails;
      }
    });
  });

  return plotAlerts;
};

export const handleCreateAnomalyData = (
  plotAnomalyAlerts: GqlAnomalyAlertsPaginated | undefined,
) => {
  if (!plotAnomalyAlerts) {
    return plotAnomalyAlerts;
  }

  plotAnomalyAlerts.anomalyDetection.content.forEach((alert) => {
    const alias = alert.sensorAlias || '';
    const depth = alert.sensorDepth || '';
    const sensorDetails =
      !!depth && !!alias ? `${depth},${alias}` : `${depth}${alias}`;

    alert.sensorDetails = sensorDetails;
    alert.fromTimestamp = `${alert.fromTimestamp || ''} - ${alert.toTimestamp || ''
      }`;

    return alert;
  });

  return plotAnomalyAlerts;
}